import graphql from "babel-plugin-relay/macro";
import { readInlineData } from "react-relay";

import type { BookingAnalyticsData } from "@/screens/BookingDetail/NewPatronBookingFlow/types";

import type {
  buildBookingConfirmationAnalyticsData_booking$data,
  buildBookingConfirmationAnalyticsData_booking$key,
} from "./__generated__/buildBookingConfirmationAnalyticsData_booking.graphql";

export type ConfirmedBooking =
  buildBookingConfirmationAnalyticsData_booking$data;

export const buildBookingConfirmationAnalyticsData = (
  analyticsData: BookingAnalyticsData,
  bookings: ReadonlyArray<ConfirmedBooking>
): BookingAnalyticsData => {
  if (!analyticsData || bookings.length === 0) return analyticsData;

  const orders = bookings.flatMap(
    (booking) =>
      booking.orders?.edges
        ?.map((edge) => edge?.node)
        .filter((order): order is NonNullable<typeof order> => !!order) ?? []
  );
  const hasAuthoritativeValue =
    orders.length > 0 &&
    orders.every(
      (order) =>
        order.total != null &&
        (analyticsData.paymentInformation?.requiresPrePayment === "FULL" ||
          order.total === 0)
    );
  const value = hasAuthoritativeValue
    ? orders.reduce((total, order) => total + (order.total ?? 0), 0) / 100
    : undefined;
  const currency =
    bookings[0].startSlot?.agenda?.facility?.department?.club?.currency ??
    undefined;
  const transactionId =
    orders.length === 1
      ? orders[0].orderPayments?.edges
          ?.map((edge) => edge?.node)
          .find(
            (payment) =>
              !!payment?.transactionId &&
              (payment.status === "APPROVED" || payment.status === "COMPLETED")
          )?.transactionId ?? undefined
      : undefined;

  return {
    ...analyticsData,
    bookingId: bookings.length === 1 ? bookings[0].id ?? undefined : undefined,
    bookingIds:
      bookings.length > 1
        ? bookings
            .map((booking) => booking.id)
            .filter((id): id is string => !!id)
        : undefined,
    orderId: orders.length === 1 ? orders[0].id ?? undefined : undefined,
    transactionId,
    value,
    currency: value != null ? currency : undefined,
  };
};

export const readBookingConfirmationAnalyticsData = (
  bookingRef:
    | buildBookingConfirmationAnalyticsData_booking$key
    | null
    | undefined
) =>
  readInlineData(
    buildBookingConfirmationAnalyticsDataFragmentString,
    bookingRef
  );

export const buildBookingConfirmationAnalyticsDataFragmentString = graphql`
  fragment buildBookingConfirmationAnalyticsData_booking on Booking @inline {
    id
    startSlot {
      agenda {
        facility {
          department {
            club {
              currency
            }
          }
        }
      }
    }
    orders {
      edges {
        node {
          id
          total
          orderPayments {
            edges {
              node {
                transactionId
                status
              }
            }
          }
        }
      }
    }
  }
`;
